// Pure layout maths for the 3D scene (no three.js import, unit-tested in Node).
// World units are roughly metres. Y is up; a floor lies in the XZ plane; the isometric camera looks
// from +X +Z, so +X+Z is "near" (bottom of the screen) and -X-Z is "far" (top of the screen).

import { PALETTE, PALETTE_KEYS, isHex, resolveColor } from './palette.js';

/** Every dimension used to lay out a floor. */
export const DIMS = Object.freeze({
  deskW: 1.5, deskD: 0.78, deskH: 0.74,
  leadDeskW: 1.9, leadDeskD: 0.92,
  seatOffset: 0.66,          // desk centre → seat centre, along the facing direction
  clusterW: 2.1,             // column pitch of a pair of facing desks
  clusterD: 4.0,             // row pitch (two desks back to back + two chairs + aisle)
  leadZone: 2.1,             // x space of the lead desk + chair
  leadGap: 0.85,             // gap between the lead desk and the team block
  podPad: 1.25,              // inner margin of a pod (props live there)
  podMin: 5.6,               // minimum pod side
  podH: 0.62,                // pod thickness
  podRadius: 0.3,
  hallR: 3.3, hallH: 0.56,
  shaftR: 0.95,
  walkW: 1.5, walkH: 0.26,
  podGap: 1.7,               // minimum clearance between pods, and between a pod and the hall
  slabMargin: 2.4, slabH: 0.5, slabRadius: 1.6,
  floorGap: 12,              // building view: vertical distance between floors
  travelGap: 44,             // floor view: other floors are pushed this far away (off screen)
});

/**
 * FNV-1a 32-bit hash of a string (deterministic across runs and platforms).
 * @param {string} str
 * @returns {number} unsigned 32-bit integer
 */
export function hashString(str) {
  let h = 0x811c9dc5;
  const s = String(str ?? '');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Small deterministic PRNG (mulberry32). Returns a function giving floats in [0, 1).
 * @param {number} seed
 * @returns {() => number}
 */
export function rng(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const HAIR_STYLES = Object.freeze(['short', 'long', 'bun', 'curly', 'cap']);
const SKINS = ['#F3D2B3', '#EFC7A2', '#E9C09B', '#DDA77E', '#C68E63', '#B07A55', '#8D5A3B', '#7A4B2C'];
const HAIRS = ['#1A1A1A', '#2B1D14', '#3B2A20', '#5A3A22', '#8C4A2F', '#A33B2B', '#C9A15B', '#D9B26B', '#E06D3A', '#6E6A66'];
const SHIRTS = [...Object.values(PALETTE), '#2F3A56', '#4A5A78', '#D9D2C3'];
const ACCENTS = ['#1D1B18', '#F4F1EA', '#E6C35C', '#E8837A', '#2F3A56', '#4FBFA8'];
const PANTS_COUNT = 5;

/**
 * The full look of an agent: fields from `agent.look` when valid, otherwise a deterministic pick
 * from a hash of the agent id (or name). Also derives `pants` (index), `headphones`, `mug`, `plant`.
 * @param {{id?:string, name?:string, look?:object}} agent
 * @returns {{skin:string, hair:string, hairStyle:string, shirt:string, accent:string,
 *   pantsIndex:number, headphones:boolean, deskProp:number, seed:number}}
 */
export function lookFor(agent) {
  const seed = hashString(agent?.id || agent?.name || 'agent');
  const r = rng(seed);
  // Always draw in the same order so a partial look stays stable.
  const pick = (list) => list[Math.floor(r() * list.length)];
  const auto = {
    skin: pick(SKINS), hair: pick(HAIRS), hairStyle: pick(HAIR_STYLES),
    shirt: pick(SHIRTS), accent: pick(ACCENTS),
  };
  const pantsIndex = Math.floor(r() * PANTS_COUNT);
  const headphones = r() < 0.22;
  const deskProp = Math.floor(r() * 4); // 0 none, 1 mug, 2 papers, 3 little plant
  const look = agent?.look && typeof agent.look === 'object' ? agent.look : {};
  const hex = (v, d) => (isHex(v) ? resolveColor(v) : d.toLowerCase());
  const hairStyle = HAIR_STYLES.includes(look.hairStyle) ? look.hairStyle : auto.hairStyle;
  return {
    skin: hex(look.skin, auto.skin),
    hair: hex(look.hair, auto.hair),
    hairStyle,
    shirt: hex(look.shirt, auto.shirt),
    accent: hex(look.accent, auto.accent),
    pantsIndex,
    headphones: hairStyle !== 'cap' && headphones,
    deskProp,
    seed,
  };
}

/**
 * Department colour: its `color` (palette key or hex), else a palette colour picked from its id.
 * @param {{id?:string, color?:string}} dept
 * @returns {string} "#rrggbb"
 */
export function deptColor(dept) {
  const fallback = PALETTE_KEYS[hashString(dept?.id || '') % PALETTE_KEYS.length];
  return resolveColor(dept?.color, fallback);
}

/**
 * Desk layout inside a pod for `count` agents. Desk 0 is the lead desk (apart, facing the team,
 * rotated to face +X); the others are pairs of facing desks (one faces +Z, the other -Z).
 * Coordinates are pod-local (pod centred on 0,0). `rot` is the yaw of the seated person
 * (facing direction = (sin rot, cos rot)); the seat is `seatOffset` behind the desk centre.
 * @param {number} count
 * Desks are created in pairs, so an odd team leaves one free desk (desks.length ≤ count + 1).
 * @returns {{w:number, d:number, desks:Array<{index:number,x:number,z:number,rot:number,lead:boolean,
 *   w:number,dd:number,seatX:number,seatZ:number}>, block:{x:number,z:number,w:number,d:number}|null,
 *   lead:{x:number,z:number}|null, cols:number, rows:number}}
 */
export function deskGrid(count) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  const D = DIMS;
  const desks = [];
  if (n === 0) return { w: D.podMin, d: D.podMin, desks, block: null, lead: null, cols: 0, rows: 0 };

  const others = n - 1;
  const clusters = Math.ceil(others / 2);
  const rows = clusters === 0 ? 0 : Math.max(1, Math.round(Math.sqrt(clusters / 1.9)));
  const cols = clusters === 0 ? 0 : Math.ceil(clusters / rows);
  const blockW = cols * D.clusterW;
  const blockD = rows * D.clusterD;
  const leadW = clusters === 0 ? D.leadZone : D.leadZone + D.leadGap;
  const totalW = leadW + blockW;
  const totalD = Math.max(blockD, D.leadDeskW + 0.6);
  const w = Math.max(D.podMin, totalW + 2 * D.podPad);
  const d = Math.max(D.podMin, totalD + 2 * D.podPad);

  // Lead: sits at the left (-X) of the block and faces +X, towards the team.
  const x0 = -totalW / 2;
  const leadSeatX = x0 + 0.55;
  const leadDeskX = leadSeatX + D.seatOffset + 0.08;
  desks.push(makeDesk(0, leadDeskX, 0, Math.PI / 2, true, D.leadDeskW, D.leadDeskD, D.seatOffset + 0.08));

  const bx0 = x0 + leadW;
  for (let c = 0; c < clusters; c++) {
    const col = c % cols;
    const row = Math.floor(c / cols);
    const cx = bx0 + (col + 0.5) * D.clusterW;
    const cz = -blockD / 2 + (row + 0.5) * D.clusterD;
    // Far desk: the person faces +Z (towards the camera). Near desk: faces -Z (screen visible).
    // When the team count is odd, the last near desk stays free (an empty seat).
    desks.push(makeDesk(desks.length, cx, cz - D.deskD / 2, 0, false, D.deskW, D.deskD, D.seatOffset));
    desks.push(makeDesk(desks.length, cx, cz + D.deskD / 2, Math.PI, false, D.deskW, D.deskD, D.seatOffset));
  }
  const block = clusters ? { x: bx0 + blockW / 2, z: 0, w: blockW, d: blockD } : null;
  return { w, d, desks, block, lead: { x: leadDeskX, z: 0 }, cols, rows };
}

function makeDesk(index, x, z, rot, lead, w, dd, seatOffset) {
  const fx = Math.sin(rot);
  const fz = Math.cos(rot);
  return {
    index, x: round(x), z: round(z), rot, lead, w, dd,
    seatX: round(x - fx * seatOffset), seatZ: round(z - fz * seatOffset),
  };
}

function round(v) {
  return Math.round(v * 1e6) / 1e6;
}

/**
 * Axis-aligned footprint of a desk and its chair (for overlap checks), pod-local.
 * @returns {{minX:number,maxX:number,minZ:number,maxZ:number}}
 */
export function deskFootprint(desk) {
  const along = Math.abs(Math.sin(desk.rot)) > 0.5; // rotated 90°: width runs along Z
  const hw = (along ? desk.dd : desk.w) / 2;
  const hd = (along ? desk.w : desk.dd) / 2;
  const box = { minX: desk.x - hw, maxX: desk.x + hw, minZ: desk.z - hd, maxZ: desk.z + hd };
  const cr = 0.34; // chair radius
  box.minX = Math.min(box.minX, desk.seatX - cr);
  box.maxX = Math.max(box.maxX, desk.seatX + cr);
  box.minZ = Math.min(box.minZ, desk.seatZ - cr);
  box.maxZ = Math.max(box.maxZ, desk.seatZ + cr);
  return box;
}

/** True when two boxes overlap, keeping `gap` apart. */
export function boxesOverlap(a, b, gap = 0) {
  return a.minX < b.maxX + gap && b.minX < a.maxX + gap && a.minZ < b.maxZ + gap && b.minZ < a.maxZ + gap;
}

/**
 * Places pods (axis-aligned rectangles) on a ring around the central hall: evenly spaced angles
 * starting at the far-right (-Z), each at the smallest radius that clears the hall, then pushed out
 * until no two pods are closer than DIMS.podGap.
 * @param {Array<{w:number,d:number}>} sizes
 * @param {{hallR?:number, gap?:number}} [opts]
 * @returns {Array<{x:number,z:number,angle:number,radius:number}>}
 */
export function ringLayout(sizes, opts = {}) {
  const hallR = opts.hallR ?? DIMS.hallR;
  const gap = opts.gap ?? DIMS.podGap;
  const n = sizes.length;
  if (n === 0) return [];
  const angles = sizes.map((_, i) => -Math.PI / 2 + (i * 2 * Math.PI) / n);
  const radii = sizes.map((s, i) => minRadius(s, angles[i], hallR + gap));
  const place = () => sizes.map((s, i) => ({
    x: Math.cos(angles[i]) * radii[i], z: Math.sin(angles[i]) * radii[i], w: s.w, d: s.d,
  }));
  for (let iter = 0; iter < 2000; iter++) {
    const pods = place();
    let moved = false;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        if (boxesOverlap(podBox(pods[i]), podBox(pods[j]), gap)) {
          radii[i] += 0.1;
          radii[j] += 0.1;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return place().map((p, i) => ({ x: round(p.x), z: round(p.z), angle: angles[i], radius: round(radii[i]) }));
}

function minRadius(size, angle, clearance) {
  const ux = Math.cos(angle);
  const uz = Math.sin(angle);
  for (let r = 0; r < 400; r += 0.05) {
    const dx = Math.max(Math.abs(r * ux) - size.w / 2, 0);
    const dz = Math.max(Math.abs(r * uz) - size.d / 2, 0);
    if (Math.hypot(dx, dz) >= clearance) return r;
  }
  return 400;
}

/** Box of a placed pod {x, z, w, d}. */
export function podBox(p) {
  return { minX: p.x - p.w / 2, maxX: p.x + p.w / 2, minZ: p.z - p.d / 2, maxZ: p.z + p.d / 2 };
}

/** Shortest distance from the origin to a box (0 when the origin is inside). */
export function distanceToBox(box) {
  const dx = Math.max(box.minX, 0, -box.maxX);
  const dz = Math.max(box.minZ, 0, -box.maxZ);
  return Math.hypot(dx, dz);
}

/**
 * Full layout of one floor from its departments (in display order) and their agent counts.
 * @param {Array<{id:string, count:number}>} depts
 * @returns {{hall:{r:number,h:number}, pods:Array<object>, walkways:Array<object>,
 *   bounds:{minX:number,maxX:number,minZ:number,maxZ:number}, slab:{x:number,z:number,w:number,d:number}}}
 */
export function floorLayout(depts) {
  const grids = depts.map((d) => deskGrid(d.count));
  const ring = ringLayout(grids.map((g) => ({ w: g.w, d: g.d })));
  const pods = depts.map((d, i) => ({
    deptId: d.id, x: ring[i].x, z: ring[i].z, w: grids[i].w, d: grids[i].d,
    angle: ring[i].angle, grid: grids[i],
  }));
  const walkways = pods.map((p) => walkwayFor(p));
  const R = DIMS.hallR;
  const bounds = { minX: -R, maxX: R, minZ: -R, maxZ: R };
  for (const p of pods) {
    const b = podBox(p);
    bounds.minX = Math.min(bounds.minX, b.minX);
    bounds.maxX = Math.max(bounds.maxX, b.maxX);
    bounds.minZ = Math.min(bounds.minZ, b.minZ);
    bounds.maxZ = Math.max(bounds.maxZ, b.maxZ);
  }
  const m = DIMS.slabMargin;
  const slab = {
    x: (bounds.minX + bounds.maxX) / 2, z: (bounds.minZ + bounds.maxZ) / 2,
    w: bounds.maxX - bounds.minX + 2 * m, d: bounds.maxZ - bounds.minZ + 2 * m,
  };
  return { hall: { r: R, h: DIMS.hallH }, pods, walkways, bounds, slab };
}

/**
 * A straight walkway from the hall edge towards the pod centre, ending under the pod edge.
 * @returns {{x:number,z:number,length:number,angle:number}} centre, length and yaw (along +X)
 */
export function walkwayFor(pod) {
  const ang = Math.atan2(pod.z, pod.x);
  const ux = Math.cos(ang);
  const uz = Math.sin(ang);
  const start = DIMS.hallR - 0.3;
  // Distance along the ray to where it enters the pod box (the largest per-axis entry), plus a
  // little so the walkway tucks under the pod.
  const tx = Math.abs(ux) > 1e-9 ? (Math.abs(pod.x) - pod.w / 2) / Math.abs(ux) : -Infinity;
  const tz = Math.abs(uz) > 1e-9 ? (Math.abs(pod.z) - pod.d / 2) / Math.abs(uz) : -Infinity;
  const edge = Math.max(tx, tz, start);
  const end = edge + 0.6;
  const length = end - start;
  const mid = (start + end) / 2;
  return { x: round(ux * mid), z: round(uz * mid), length: round(length), angle: ang };
}

/**
 * Common footprint of the building: the union of every floor's slab, so that stacked plates
 * line up and the pillars at their corners join floor to floor.
 * @param {Array<{slab:{x:number,z:number,w:number,d:number}}>} layouts
 * @returns {{x:number,z:number,w:number,d:number}}
 */
export function buildingSlab(layouts) {
  if (!layouts.length) return { x: 0, z: 0, w: 2 * (DIMS.hallR + DIMS.slabMargin), d: 2 * (DIMS.hallR + DIMS.slabMargin) };
  let minX = Infinity; let maxX = -Infinity; let minZ = Infinity; let maxZ = -Infinity;
  for (const { slab: s } of layouts) {
    minX = Math.min(minX, s.x - s.w / 2); maxX = Math.max(maxX, s.x + s.w / 2);
    minZ = Math.min(minZ, s.z - s.d / 2); maxZ = Math.max(maxZ, s.z + s.d / 2);
  }
  return { x: (minX + maxX) / 2, z: (minZ + maxZ) / 2, w: maxX - minX, d: maxZ - minZ };
}

/**
 * Vertical position of each floor group.
 * Floor view: the current floor sits at 0, the others far above/below (off screen).
 * Building view: floors stacked DIMS.floorGap apart, the current floor still at 0.
 * @param {number} floorCount
 * @param {number} current index of the current floor (0 = lowest)
 * @param {'floor'|'building'} mode
 * @returns {number[]}
 */
export function floorOffsets(floorCount, current, mode) {
  const gap = mode === 'building' ? DIMS.floorGap : DIMS.travelGap;
  const out = [];
  for (let i = 0; i < floorCount; i++) out.push((i - current) * gap);
  return out;
}

/**
 * Orders an office for display: floors by `order` then name; departments of each floor by order;
 * agents of each department with the lead first (agent.lead or dept.leadAgentId: desk 0, the lead
 * desk), then by order of appearance in office.agents, which is the order the admin chose (PUT
 * /api/departments/:id/agent-order). Departments or agents pointing at unknown parents are dropped.
 * @param {{floors?:Array, departments?:Array, agents?:Array}} office
 * @returns {Array<{floor:object, depts:Array<{dept:object, agents:Array<object>}>}>}
 */
export function organizeOffice(office) {
  const floors = [...(office?.floors || [])].filter((f) => f && f.id);
  floors.sort((a, b) => (num(a.order) - num(b.order)) || String(a.name).localeCompare(String(b.name)));
  const depts = (office?.departments || []).filter((d) => d && d.id);
  const agents = (office?.agents || []).filter((a) => a && a.id);
  return floors.map((floor) => {
    const fd = depts.filter((d) => d.floorId === floor.id);
    fd.sort((a, b) => (num(a.order) - num(b.order)) || String(a.name).localeCompare(String(b.name)));
    return {
      floor,
      depts: fd.map((dept) => {
        const list = agents.filter((a) => a.deptId === dept.id);
        const leadId = list.find((a) => a.id === dept.leadAgentId)?.id ?? list.find((a) => a.lead)?.id;
        list.sort((a, b) => (a.id === leadId ? -1 : b.id === leadId ? 1 : 0));
        return { dept, agents: list };
      }),
    };
  });
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The number shown for a floor (elevator, cabin display, building view): its real position in the
 * building, `order + 1`, so a member who sees only some floors reads the same number as an admin.
 * Falls back to the display position (`index + 1`) when the order is not a whole number 0–998.
 * @param {{order?:number}} floor
 * @param {number} index position among the floors shown (0-based)
 * @returns {number}
 */
export function floorNumber(floor, index = 0) {
  const o = Number(floor?.order);
  if (Number.isInteger(o) && o >= 0 && o <= 998) return o + 1;
  const i = Number(index);
  return Number.isInteger(i) && i >= 0 ? i + 1 : 1;
}

/**
 * A structural signature of the office: when it is unchanged, the geometry does not need a rebuild.
 * @param {{floors?:Array, departments?:Array, agents?:Array}} office
 * @returns {string}
 */
export function officeSignature(office) {
  const org = organizeOffice(office);
  return JSON.stringify(org.map(({ floor, depts }) => [
    floor.id, floor.name, depts.map(({ dept, agents }) => [
      dept.id, dept.name, dept.color, agents.map((a) => [a.id, a.name, a.lead ? 1 : 0, a.look || null]),
    ]),
  ]));
}
